/* ---------------------------------------------------------------------------
   What a defect marker looks like, wherever a `.glb` is drawn.

   ONE ANSWER FOR BOTH VIEWS. The Defect Inspector (src/inspector/scene.ts) and the
   record's own 3D view (./meshView.ts) draw the same pins on the same file, and a
   Severe crack that was orange on one and yellow on the other would be two
   different findings to anybody comparing them. So the size and the two ramps live
   here, in a module with no DOM and no three.js, and both import them.

   Lifted out of src/inspector/scene.ts, which re-exports the ramps so nothing that
   read them there has to change.
   --------------------------------------------------------------------------- */

/** Pin radius in metres, so a marker reads at the same size on any bridge. */
export const PIN_RADIUS = 0.45;

/**
 * How severity colours a pin, against a dark ground.
 *
 * The one place in this application that uses traffic-light colour rather than
 * the monochrome accent ramp, and for the same reason the flat map's screening
 * layer does: four steps of one blue are not distinguishable as small marks over
 * arbitrary grey geometry. Ordered worst-first, matching DEFECT_SEVERITIES read
 * backwards.
 *
 * Unchanged from when this was the only ramp. The viewport was dark whatever the
 * register was set to, so these four are what has been seen and reported against,
 * and nothing about a light ground being added is a reason to retune them.
 */
export const SEVERITY_COLOUR: Record<string, number> = {
  Critical: 0xc0392b,
  Severe: 0xe07b39,
  Moderate: 0xd9b310,
  Minor: 0x4f8a5b,
};

/**
 * And against a light one.
 *
 * THE SAME FOUR THE REGISTER ALREADY DRAWS ON A LIGHT PANEL — `--sev-1` to
 * `--sev-4` in src/styles/field.css, whose own comment claims they are "the same
 * colours the Defect Inspector paints its 3D markers with". That was true of
 * neither ramp before, because there was only one and it was the dark one; a
 * defect is now one colour wherever it appears at a given theme.
 *
 * The dark ramp on a light ground is what makes this necessary rather than tidy.
 * Measured against --md-surface-variant (#d8dadd): the dark Moderate is a 1.44:1
 * mark there, which is a pin nobody finds. This set reads 4.67:1 Critical,
 * 2.97:1 Severe, 2.93:1 Minor.
 *
 * Moderate is still the weakest at 1.73:1, and that is a limit of the palette
 * rather than of this view — it is a yellow on a pale grey, the same pairing the
 * field record's own severity dots already ship at, and moving it would move the
 * register's colour for Moderate everywhere. If it proves too weak on a deck in
 * daylight, the fix is a rim on the marker rather than a fifth yellow.
 */
export const SEVERITY_COLOUR_LIGHT: Record<string, number> = {
  Critical: 0xb3261e,
  Severe: 0xc1622f,
  Moderate: 0xc9a227,
  Minor: 0x4f8a5b,
};

/** What a pin gets when its severity is not one of the four, per ground. */
const UNKNOWN_COLOUR = 0x8a8a8d;
const UNKNOWN_COLOUR_LIGHT = 0x6d7276;

/** The colour for one severity on a known ground. */
export function severityRamp(severity: string, light: boolean): number {
  return light
    ? (SEVERITY_COLOUR_LIGHT[severity] ?? UNKNOWN_COLOUR_LIGHT)
    : (SEVERITY_COLOUR[severity] ?? UNKNOWN_COLOUR);
}
